/* eslint-disable no-restricted-imports */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import { AttachFile, BusinessCenter, CloudUpload, Description } from '@material-ui/icons';

import { xpcellService } from 'services';
import { socket } from 'services/socket';
import XLSX from 'xlsx';

import { useStyles } from '../styles';
import { ComponentsNotFoundDialog } from './ComponentsNotFoundDialog';
import { ProgressDialog } from './ProgressDialog';

export function UploadFile({
  selectedProcesses,
  file,
  handleResetFileImport,
  processOptions,
  handleFileChange,
  handleProcessChange,
}) {
  const classes = useStyles();

  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [progressMessage, setProgressMessage] = useState('');
  const [notFoundComponents, setNotFoundComponents] = useState([]);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showNotFoundModal, setShowNotFoundModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const socketIdRef = useRef();

  useEffect(() => {
    if (!socket.connected) {
      socket.connect();
    }

    socket.on('connect', () => {
      socketIdRef.current = socket.id;
    });

    socket.on('upload-progress', (data) => {
      setProgress(data.percentage);
      setProgressMessage(data.message);
      setIsProcessing(true);
      setShowProgressModal(true);

      if (data.percentage === 100) {
        handleResetFileImport();
        setIsProcessing(false);
        setProgress(null);
        setShowProgressModal(false);
      }
    });

    socket.on('upload-complete', () => {
      setIsLoading(false);
    });

    return () => {
      socket.off('upload-progress');
      socket.off('component-not-found');
      socket.off('upload-complete');
      socket.disconnect();
    };
  }, [handleResetFileImport, notFoundComponents]);

  const handleDownloadTemplate = async () => {
    const wb = XLSX.utils.book_new();

    const ws = XLSX.utils.json_to_sheet(
      [
        {
          a: 'PART NUMBER',
          b: 'VALOR VENDA',
          c: 'VALOR COMPRA/CUSTO',
        },
      ],
      {
        skipHeader: 1,
      },
    );

    XLSX.utils.book_append_sheet(wb, ws, 'No Header');

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), 'Layout.xlsx');
  };

  const handleUpload = async () => {
    if (!file || selectedProcesses.length === 0) return;

    setIsLoading(true);
    setNotFoundComponents([]);
    setShowProgressModal(true);

    try {
      const reader = new FileReader();

      reader.onload = async (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

        const newProducts = jsonData.map((row) => ({
          part_number: row['PART NUMBER'],
          sell_price: parseFloat(row['VALOR VENDA']),
          cost_price: parseFloat(row['VALOR COMPRA/CUSTO']),
        }));

        const { data: response } = await xpcellService.updateManyComponentSellProcess({
          components: newProducts,
          process: selectedProcesses,
          socketId: socketIdRef?.current,
        });

        handleResetFileImport();
        setIsProcessing(false);
        setProgress(null);
        setShowProgressModal(false);

        if (response && response.length > 0) {
          setNotFoundComponents(response);
          setShowNotFoundModal(true);
        }
      };

      reader.onerror = (error) => {
        toast.error('Erro ao ler o arquivo:', error);
        setIsLoading(false);
        setShowProgressModal(false);
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setShowProgressModal(false);
    }
  };

  const handleCloseProgressModal = () => {
    if (progress === 100 || !isProcessing) {
      setShowProgressModal(false);
    }
  };

  return (
    <>
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <Typography variant='h6' gutterBottom style={{ fontWeight: 600 }}>
            Atualização em Massa
          </Typography>
          <Typography variant='body2' color='textSecondary' gutterBottom>
            Faça upload de uma planilha para atualizar vários produtos de uma vez
          </Typography>

          <Box display='flex' justifyContent='flex-end' mb={2}>
            <Link
              component='button'
              variant='body2'
              onClick={handleDownloadTemplate}
              color='primary'
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <Description fontSize='small' style={{ marginRight: 4 }} />
              Baixar modelo da planilha
            </Link>
          </Box>

          <Box
            className={classes.uploadArea}
            onClick={() => document.getElementById('spreadsheet-upload').click()}
          >
            <input
              accept='.xlsx,.xls,.csv'
              style={{ display: 'none' }}
              id='spreadsheet-upload'
              type='file'
              onChange={handleFileChange}
            />

            {file ? (
              <div>
                <AttachFile fontSize='large' style={{ color: '#4caf50' }} />
                <Typography variant='subtitle1' style={{ marginTop: 8 }}>
                  {file.name}
                </Typography>
                <Typography variant='body2' color='textSecondary'>
                  Clique para alterar o arquivo
                </Typography>
              </div>
            ) : (
              <div>
                <CloudUpload fontSize='large' color='primary' />
                <Typography variant='subtitle1' style={{ marginTop: 8 }}>
                  Arraste ou clique para selecionar um arquivo
                </Typography>
                <Typography variant='body2' color='textSecondary'>
                  Formatos suportados: .xlsx, .xls, .csv
                </Typography>
              </div>
            )}
          </Box>

          <Grid container spacing={3} size='small' style={{ marginTop: 16 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size='small' variant='outlined'>
                <InputLabel>Processos para aplicar</InputLabel>
                <Select
                  multiple
                  value={selectedProcesses}
                  onChange={handleProcessChange}
                  renderValue={(selected) => (
                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={processOptions.find((p) => p.value === value)?.label || value}
                          className={classes.processChip}
                          size='small'
                        />
                      ))}
                    </div>
                  )}
                >
                  {processOptions.map((process) => (
                    <MenuItem key={process.value} value={process.value}>
                      <Checkbox checked={selectedProcesses.indexOf(process.value) > -1} />
                      <ListItemText primary={process.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} style={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant='contained'
                color='primary'
                size='large'
                startIcon={
                  isLoading ? <CircularProgress size={20} color='inherit' /> : <BusinessCenter />
                }
                onClick={handleUpload}
                disabled={!file || selectedProcesses.length === 0 || isLoading}
                style={{ width: '100%' }}
              >
                {isLoading ? 'Processando...' : 'Aplicar Atualização'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <ProgressDialog
        handleClose={handleCloseProgressModal}
        isOpen={showProgressModal}
        isProcessing={isProcessing}
        progress={progress}
        progressMessage={progressMessage}
      />

      <ComponentsNotFoundDialog
        isOpen={showNotFoundModal}
        handleClose={() => setShowNotFoundModal(false)}
        notFoundComponents={notFoundComponents}
      />
    </>
  );
}
